import { memo } from 'react';
import { AppButton } from '../Button/_index';
import { Badge } from '../Kit/_index';
import { FieldSelect } from '../Form/_Fields/FieldSelect';

const BLOCK = 'LStudio__app_kanban';

const KanbanCard = memo(({ card, board, columns, renderCard, renderMoveControl }) => (
    <li
        className={`${BLOCK}__card`}
        draggable
        data-dragging={board.dragId === card.id}
        data-tone={card.tone ?? 'neutral'}
        onDragStart={(e) => {
            board.setDragId(card.id);
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', card.id);
        }}
        onDragEnd={() => board.setDragId(null)}
    >
        <div className={`${BLOCK}__card_body`}>
            {renderCard
                ? renderCard(card)
                : <><p className={`${BLOCK}__card_title`}>{card.title}</p>{card.meta && <span className={`${BLOCK}__card_meta`}>{card.meta}</span>}</>}
        </div>

        {renderMoveControl
            ? renderMoveControl(card, columns, board)
            : (
                <div className={`${BLOCK}__move`}>
                    <FieldSelect isBare
                        value={card.columnId}
                        options={columns.map((col) => ({ value: col.id, label: col.label }))}
                        isSearchable={false}
                        onChange={(e) => board.onMoveCard?.(card.id, e.target.value)}
                    />
                </div>
            )}
    </li>
), (prev, next) => (
    prev.card === next.card
    && prev.columns === next.columns
    && prev.renderCard === next.renderCard
    && prev.renderMoveControl === next.renderMoveControl
    && (prev.board.dragId === prev.card.id) === (next.board.dragId === next.card.id)
));

KanbanCard.displayName = 'KanbanCard';

export const AppKanban = ({
    board,
    columns = [],
    renderCard = null,
    renderMoveControl = null,
    renderColumnActions = null,
    renderColumnDescription = null,
    onAddCard = null,
    emptyLabel = 'Sin tarjetas',
}) => (
    <div className={BLOCK}>
        {columns.map((column) => {
            const CARDS = board.onGetColumnCards(column.id);
            const IS_OVER_LIMIT = column.limit && CARDS.length > column.limit;

            return (
                <section
                    key={column.id}
                    className={`${BLOCK}__column`}
                    data-tone={column.tone ?? 'neutral'}
                    data-over={board.overColumn === column.id}
                    data-limit={IS_OVER_LIMIT}
                    onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (board.overColumn !== column.id) board.setOverColumn(column.id);
                    }}
                    onDragLeave={(e) => {
                        if (e.currentTarget.contains(e.relatedTarget)) return;
                        board.setOverColumn(null);
                    }}
                    onDrop={(e) => {
                        e.preventDefault();
                        board.onDrop(column.id);
                    }}
                >
                    <header className={`${BLOCK}__column_head`}>
                        <div className={`${BLOCK}__column_identity`}>
                            <span className={`${BLOCK}__column_title`}>{column.label}</span>
                            {renderColumnDescription?.(column, CARDS)}
                        </div>
                        <div className={`${BLOCK}__column_tools`}>
                            <Badge tone={IS_OVER_LIMIT ? 'danger' : 'neutral'} size="sm">
                                {column.limit ? `${CARDS.length}/${column.limit}` : CARDS.length}
                            </Badge>
                            {renderColumnActions?.(column, CARDS)}
                        </div>
                    </header>

                    <ul className={`${BLOCK}__list`}>
                        {CARDS.length === 0
                            ? <li className={`${BLOCK}__empty`}>{emptyLabel}</li>
                            : CARDS.map((card) => (
                                <KanbanCard key={card.id} card={card} board={board}
                                    columns={columns} renderCard={renderCard} renderMoveControl={renderMoveControl} />
                            ))}
                    </ul>

                    {onAddCard && (
                        <AppButton variant="ghost" tone="neutral" size="sm" isFullWidth onClick={() => onAddCard(column)}>
                            + Agregar
                        </AppButton>
                    )}
                </section>
            );
        })}
    </div>
);
